import React from "react";
import LegalLayout from "../../components/LegalLayout";
import useDocumentTitle from "../../hooks/useDocumentTitle";
import { ShieldAlert, Handshake, Bug, Award } from "lucide-react";

const SECTIONS = [
  { id: "reporting",    label: "How to report" },
  { id: "safe-harbor",  label: "Safe harbor" },
  { id: "in-scope",     label: "In scope" },
  { id: "out-of-scope", label: "Out of scope" },
  { id: "not-do",       label: "What NOT to do" },
  { id: "recognition",  label: "Recognition" },
  { id: "pgp",          label: "PGP & security.txt" },
  { id: "contact",      label: "Contact" },
];

const PILLARS = [
  { icon: ShieldAlert,    title: "Crypto-grade security bar", desc: "QorTrace is a cryptographic-risk product, so we hold ourselves higher than typical SaaS. Every admin role requires hardware-backed TOTP 2FA.", accent: "var(--qt-cyan)" },
  { icon: Handshake,      title: "Safe harbor",                desc: "We will not pursue civil or criminal action against good-faith researchers who follow this policy. We will work with you on coordinated disclosure.", accent: "#1FDD83" },
  { icon: Bug,            title: "Triage in 5 business days", desc: "We acknowledge reports within 2 business days and provide a triage decision within 5. Fix windows depend on severity — Critical = 24h, High = 7d, Medium = 30d.", accent: "var(--qt-amber)" },
  { icon: Award,          title: "Hall of Fame & swag",       desc: "Valid, novel reports get permanent listing in our Researcher Hall of Fame on this page (with your consent). Standout reports get QorTrace-branded gear shipped worldwide.", accent: "var(--qt-cyan)" },
];

export default function Security() {
  useDocumentTitle("Security & Responsible Disclosure — QorTrace");
  return (
    <LegalLayout
      eyebrow="SECURITY · DISCLOSURE"
      title="Security &amp; Responsible Disclosure"
      lastUpdated="February 2026"
      version="qortrace-security-v1.0"
      status="ACTIVE"
      intro="QorTrace is a cryptographic-risk product, so we hold ourselves to a high security bar. We welcome reports from the security community and commit to working in good faith with researchers who help us keep QorTrace safe."
      stats={[
        { value: "2d",  label: "ACK SLA" },
        { value: "5d",  label: "TRIAGE SLA" },
        { value: "24h", label: "CRITICAL FIX SLA" },
        { value: "1.0", label: "POLICY VERSION" },
      ]}
      pillars={PILLARS}
      sections={SECTIONS}
    >
      <p>
        QorTrace is a cryptographic-risk product, so we hold ourselves to a
        high security bar. We welcome reports from the security community and
        commit to working in good faith with researchers who help us keep
        QorTrace safe.
      </p>

      <h2 id="reporting">1. Reporting a vulnerability</h2>
      <p>
        Email <a href="mailto:security@qortrace.com">security@qortrace.com</a>{" "}
        with a clear write-up of the issue, reproduction steps, affected URLs
        or endpoints, and any proof-of-concept code. Encrypt sensitive details
        with our PGP key:
      </p>
      <ul>
        <li>
          PGP fingerprint: available on request — email{" "}
          <a href="mailto:security@qortrace.com" style={{ textDecoration: "underline" }}>
            security@qortrace.com
          </a>{" "}
          and we&apos;ll send our latest public key over a side-channel for
          coordinated-disclosure traffic.
        </li>
        <li>
          security.txt (RFC 9116):{" "}
          <a href="https://qortrace.com/.well-known/security.txt"
             style={{ textDecoration: "underline" }}>
            https://qortrace.com/.well-known/security.txt
          </a>
        </li>
      </ul>
      <p>
        We acknowledge reports within <strong>2 business days</strong> and
        provide a triage decision within <strong>5 business days</strong>.
      </p>

      <h2 id="safe-harbor">2. Safe harbor</h2>
      <p>
        We will not pursue civil or criminal action against researchers who:
      </p>
      <ul>
        <li>Make a good-faith effort to comply with this policy.</li>
        <li>
          Avoid privacy violations, destruction of data, or interruption /
          degradation of the Service.
        </li>
        <li>
          Only interact with accounts they own or have explicit permission
          from the account holder to access.
        </li>
        <li>
          Stop testing as soon as the vulnerability is identified and provide
          us a reasonable time to remediate before public disclosure.
        </li>
      </ul>

      <h2 id="in-scope">3. In scope</h2>
      <ul>
        <li>
          The QorTrace web application at <code>qortrace.com</code> and{" "}
          <code>*.qortrace.com</code>.
        </li>
        <li>
          The QorTrace API at <code>api.qortrace.com</code> and{" "}
          <code>qortrace.com/api</code>.
        </li>
        <li>Customer-facing audit certificates and verification endpoints.</li>
      </ul>

      <h2 id="out-of-scope">4. Out of scope</h2>
      <ul>
        <li>
          Findings that require physical access, social engineering of
          QorTrace staff, or denial-of-service techniques.
        </li>
        <li>
          Reports based solely on automated scanner output without a working
          proof-of-concept.
        </li>
        <li>
          Best-practice configuration suggestions without a concrete impact
          (e.g. missing HSTS preload, weak ciphers in non-customer-facing
          internal tooling).
        </li>
        <li>
          Vulnerabilities in third-party services (Stripe, Cloudflare,
          Resend, etc.) &mdash; please report those directly to those
          providers.
        </li>
        <li>
          Spam, content-injection, or self-XSS that requires the victim to
          paste attacker-controlled code into devtools.
        </li>
      </ul>

      <h2 id="not-do">5. What we ask you NOT to do</h2>
      <ul>
        <li>
          Do not access, modify, or delete data that does not belong to you.
        </li>
        <li>
          Do not run automated load tests, scraping at scale, or fuzzing
          against production.
        </li>
        <li>Do not publicly disclose findings before we&rsquo;ve remediated.</li>
      </ul>

      <h2 id="recognition">6. Recognition</h2>
      <p>
        With your permission, we publicly credit researchers who report
        valid, in-scope issues on a hall-of-fame page after remediation.
        QorTrace does not currently operate a paid bug-bounty program but we
        may award discretionary bounties or merch for impactful reports.
      </p>

      <h2 id="pgp">7. PGP &amp; security.txt</h2>
      <p>
        Our <code>/.well-known/security.txt</code> will be published at
        launch and will reference the canonical contact channel. If you find
        a discrepancy between this page and <code>security.txt</code>, the
        latter is authoritative.
      </p>

      <h2 id="contact">8. Contact</h2>
      <p>
        Reports: <a href="mailto:security@qortrace.com">security@qortrace.com</a>
        <br />
        Privacy: <a href="mailto:privacy@qortrace.com">privacy@qortrace.com</a>
        <br />
        Press &amp; partnerships:{" "}
        <a href="mailto:press@qortrace.com">press@qortrace.com</a>
      </p>
    </LegalLayout>
  );
}
